import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, Card, MenuItem, Popover, TextField, Typography} from "@mui/material";
import useSavedComponentsState from "../../../hooks/use-saved-components-state";
import BroadcastService from "../../../../core/services/broadcast";
import {deepEqual} from "../../../../core/services/utils/utils";

export const ProductDimensionUnit = ({unit, backgroundColor, inInput}) => {
    return (
        <Box sx={{
            backgroundColor: backgroundColor ?? "background.default",
            color: 'tertiary.main',
            padding: 1,
            marginInlineEnd: inInput ? -1 : 0,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
        }}>
            <Typography fontWeight={600} variant={"caption"} component={'p'}>
                {unit ?? ""}
            </Typography>
        </Box>
    )
};

export const DimensionMetricController = () => {
    const [anchor, setAnchor] = useState(null);
    const {
        lengthUnit, changeLengthUnit, supportedLengthUnits,
        weightUnit, changeWeightUnit, supportedWeightUnits,
    } = useSelectedDimensionMetrics();


    return (
        <>
            <Card
                onClick={(e) => setAnchor(e.currentTarget)}
                variant={'elevation'}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: 'none',
                    backgroundColor: 'secondary.light',
                    borderRadius: 1,
                    paddingInline: 2,
                    paddingBlock: 1,
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Typography variant={'body2'} fontWeight={600}>
                        Length Unit:
                    </Typography>
                    <ProductDimensionUnit unit={lengthUnit}/>
                </Box>
                |
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Typography variant={'body2'} fontWeight={600}>
                        Weight Unit:
                    </Typography>
                    <ProductDimensionUnit unit={weightUnit}/>
                </Box>
            </Card>
            <Popover
                id={'dimension-metric-popover'}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorEl={anchor}
                elevation={3}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        width: 'min(95vw, 400px)',
                        padding: 1,
                    }}
                >
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Select Dimension Metrics
                    </Typography>
                    <Typography variant={'caption'}>
                        Choose your preferred units for length and weight measurements.
                    </Typography>
                    <TextField
                        sx={{marginBlock: 1}}
                        select
                        fullWidth
                        size={'small'}
                        label={'Length Unit'}
                        value={lengthUnit}
                        onChange={(e) => changeLengthUnit(e.target.value)}
                        SelectProps={{renderValue: (value) => supportedLengthUnits.find(e => e.value === value)?.title ?? ""}}
                    >
                        {
                            supportedLengthUnits.map(unit => (
                                <MenuItem key={unit.value} value={unit.value}>
                                    {unit.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>

                    <TextField
                        sx={{marginBlock: 1}}
                        select
                        fullWidth
                        size={'small'}
                        label={'Weight Unit'}
                        value={weightUnit}
                        onChange={(e) => changeWeightUnit(e.target.value)}
                        SelectProps={{renderValue: (value) => supportedWeightUnits.find(e => e.value === value)?.title ?? ""}}
                    >
                        {
                            supportedWeightUnits.map(unit => (
                                <MenuItem key={unit.value} value={unit.value}>
                                    {unit.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                    <div className={'mt-2'}/>
                    <Button variant={'contained'} fullWidth onClick={() => setAnchor(null)}>
                        Done
                    </Button>
                    <div className={'mt-1'}/>
                </Box>
            </Popover>
        </>
    )
}

/**
 * Hook to manage selected dimension metrics (length and weight units)
 */
export const useSelectedDimensionMetrics = () => {
    const defaultLengthUnit = useMemo(() => 'mm', []);
    const defaultWeightUnit = useMemo(() => 'kg', []);

    const dimensionLengthUnits = useMemo(() => [
        {value: 'mm', title: 'Millimeters (mm)'},
        {value: 'cm', title: 'Centimeters (cm)'},
        {value: 'm', title: 'Meters (m)'},
        {value: 'in', title: 'Inches (in)'},
        {value: 'ft', title: 'Feet (ft)'},
    ], []);

    const dimensionWeightUnits = useMemo(() => [
        {value: 'g', title: 'Grams (g)'},
        {value: 'kg', title: 'Kilograms (kg)'},
        {value: 'oz', title: 'Ounces (oz)'},
        {value: 'lb', title: 'Pounds (lb)'},
    ], []);

    const [dimensionUnits, setDimensionUnits] = useState({length: defaultLengthUnit, weight: defaultWeightUnit});
    const {highestVersion: getCachedDimensionMetrics, save: saveCachedDimensionMetrics} = useSavedComponentsState({
        name: "selected-dimension-metrics",
        version: "1.0.0"
    });

    /**
     * Listens for the advanced search data changes.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.selectedDimensionMetrics, 'message', (val) => {
            setDimensionUnits(prevState => {
                if (deepEqual(prevState, val)) {
                    return prevState;
                }
                return val;
            })
        });
        return () => {
            bcClose();
        }
    }, []);

    /**
     * Fetches the cached dimension metrics.
     * @type {(function(): Promise<void>)}
     */
    const getDimensionMetrics = useCallback(async () => {
        const cachedMode = await getCachedDimensionMetrics();
        if (cachedMode?.data) {
            setDimensionUnits(cachedMode.data);
        } else {
            setDimensionUnits({length: defaultLengthUnit, weight: defaultWeightUnit});
        }
    }, [defaultLengthUnit, defaultWeightUnit, getCachedDimensionMetrics]);

    /**
     * Loads the cached view mode on the component mount.
     */
    useEffect(() => {
        void getDimensionMetrics();
    }, [getDimensionMetrics]);

    /**
     * Change the length unit
     * @type {(function(*): void)}
     */
    const changeLengthUnit = useCallback((newUnit) => {
        setDimensionUnits((prev) => {
            const updated = {...prev, length: newUnit};
            void saveCachedDimensionMetrics(updated);
            BroadcastService.postMessage(BroadcastService.channelNames.selectedDimensionMetrics, updated);
            return updated;
        });
    }, [saveCachedDimensionMetrics]);

    /**
     * Change the weight unit
     * @type {(function(*): void)}
     */
    const changeWeightUnit = useCallback((newUnit) => {
        setDimensionUnits((prev) => {
            const updated = {...prev, weight: newUnit};
            void saveCachedDimensionMetrics(updated);
            BroadcastService.postMessage(BroadcastService.channelNames.selectedDimensionMetrics, updated);
            return updated;
        });
    }, [saveCachedDimensionMetrics]);

    /**
     * Transform weight value between units
     * @type {(function(*, {fromDefault?: boolean, toDefault?: boolean, display?: boolean, displayDecimals?: number}): void)}
     */
    const transformLengthValue = useCallback((value, {fromDefault = false, toDefault = false, display = true, displayDecimals = 3}) => {
        if (fromDefault && toDefault) {
            throw new Error("Cannot transform from default to default.");
        }
        let res;
        if (fromDefault) {
            // Transform from default (mm) to the currently selected unit.
            const rates = {
                'mm': 1,
                'cm': 0.1,
                'm': 0.001,
                'in': 0.0393701,
                'ft': 0.00328084,
            };
            res = value * (rates[dimensionUnits.length] || 1);
        } else if (toDefault) {
            // Transform from the currently selected unit to default (mm).
            const rates = {
                'mm': 1,
                'cm': 10,
                'm': 1000,
                'in': 25.4,
                'ft': 304.8,
            };
            // since we are converting to default, we multiply by the rate without any trucation.
            res = value * (rates[dimensionUnits.length] || 1);
        } else {
            res = value;
        }
        if (display) {
            res = res.toFixed(displayDecimals);
            // TODO: include different localization formats for the decimal point.
            res = res.includes(".") ? res.replace(/\.?0+$/, '') : res;
        }
        return res;
    }, [dimensionUnits.length]);

    /**
     * Transform weight value between units
     * @type {(function(*, {fromDefault?: boolean, toDefault?: boolean}): void)}
     */
    const transformWeightValue = useCallback((value, {fromDefault = false, toDefault = false, display = fromDefault, displayDecimals = 3}) => {
        if (fromDefault && toDefault) {
            throw new Error("Cannot transform from default to default.");
        }
        let res;
        if (fromDefault) {
            // Transform from default (kg) to the currently selected unit.
            const rates = {
                'g': 1000,
                'kg': 1,
                'lb': 2.20462,
                'oz': 35.274,
            };
            res = value * (rates[dimensionUnits.weight] || 1);
        } else if (toDefault) {
            // Transform from the currently selected unit to default (kg).
            const rates = {
                'g': 0.001,
                'kg': 1,
                'lb': 0.453592,
                'oz': 0.0283495,
            };
            // since we are converting to default, we multiply by the rate without any trucation.
            res = value * (rates[dimensionUnits.weight] || 1)
        } else {
            res = value;
        }
        if (display) {
            res = res.toFixed(displayDecimals);
            res = res.replace(/\.?0+$/, '');
        }
        return res;
    }, [dimensionUnits.weight]);

    return {
        defaultWeightUnit,
        defaultLengthUnit,
        lengthUnit: dimensionUnits.length,
        supportedLengthUnits: dimensionLengthUnits,
        weightUnit: dimensionUnits.weight,
        supportedWeightUnits: dimensionWeightUnits,
        changeLengthUnit,
        changeWeightUnit,
        transformLengthValue,
        transformWeightValue,
    };
}